import { Fragment, ReactNode } from 'react';
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    Transition,
    TransitionChild,
} from '@headlessui/react';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { twMerge } from 'tailwind-merge';
import Button from './Button';
import { RowTabStopContext } from './row-tab-stop';

interface Props {
    open: boolean;
    header?: ReactNode;
    children: ReactNode;
    footer?: ReactNode;
    className?: string;
    onClose?: () => void;
}

const SimpleModalContent = ({
    open,
    header,
    children,
    footer,
    className,
    onClose,
}: Props) => {
    const { i18next } = useAppContext();
    const { previousModal } = useModal();

    const handleClose = () => {
        onClose?.();
        previousModal?.();
    };

    return (
        <Transition show={open} as={Fragment}>
            <Dialog
                static={true}
                as="div"
                className="relative z-20"
                onClose={handleClose}
            >
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900 opacity-70 transition-opacity dark:bg-gray-100" />
                </TransitionChild>

                <div className="fixed inset-0 z-30 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel
                                className={twMerge(
                                    'shadow-base relative flex max-h-[80dvh] transform flex-col overflow-hidden rounded-sm bg-white text-start transition-all dark:bg-gray-200',
                                    className,
                                )}
                                data-test-id="modal-dialog"
                            >
                                <header
                                    className={twMerge(
                                        'flex h-15 shrink-0 items-center px-4',
                                        header
                                            ? 'justify-between'
                                            : 'justify-end',
                                    )}
                                >
                                    {header ? (
                                        <DialogTitle
                                            as="h1"
                                            className="text-2xl font-bold lowercase first-letter:uppercase"
                                        >
                                            {header}
                                        </DialogTitle>
                                    ) : null}
                                    <Button
                                        iconKey="close"
                                        color="icon"
                                        aria-label={i18next.t('close')}
                                        onClick={handleClose}
                                        data-test-id="modal-close-button"
                                    />
                                </header>
                                <div className="scrollbar flex-1 overflow-y-auto px-4">
                                    {children}
                                </div>
                                {footer && (
                                    <div className="shrink-0 px-4 pb-4">
                                        {footer}
                                    </div>
                                )}
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

// Context passes through portals, so a dialog that a list row opens would
// otherwise take the row's Tab stop state.
const SimpleModal = (props: Props) => (
    <RowTabStopContext.Provider value={undefined}>
        <SimpleModalContent {...props} />
    </RowTabStopContext.Provider>
);

export default SimpleModal;
